import React, { useState } from "react"
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import Image, { ImagePropTypes } from "@src/components/core-image"
import { PricePropTypes } from "@src/graphql-fragments/prices"
import ImageWrapper from "@src/components/core-image-wrapper"
import Icon from "@src/components/core-icon"
import TagList from "@src/components/core-tag-list"
import Currency from "@src/components/core-value-local-currency"
import CalendarMonths from "@src/components/core-calendar-months"
import HtmlContent from "@src/components/core-value-html"
import Button from "@src/components/core-button"
import ImageGallery from "@src/components/core-image-gallery"
import ClickableTileWrapper from "@src/components/core-clickable-tile-wrapper"
import { convertToMonths } from "@src/utils/convert-number-to-month"
import { DUMMY_MAP_TITLE } from "@src/utils/constants/tour-map"
import IconDetailContentTile from "@src/components//core-icon-detail-tile"
import { TOUR_CATEGORY_ICONS } from "@src/utils/constants/tour-category-icons"
import { checkTourTnC } from "@src/utils/tour-tnc"

const LinkWrapper = styled.a`
  all: unset;
  cursor: pointer;
`

const StyledClickableWrapper = styled(ClickableTileWrapper)`
  flex: 0 0 100%;

  @media (max-width: 450px) {
    max-width: 384px;
    width: 100%;
    flex-basis: 100%;
  }

  @media (max-width: 330px) {
    max-width: 280px;
  }

  ${props =>
    props.$variant === "vertical"
      ? css`
          flex: 0 0 300px;
          height: 480px;
          & > a.trigger:hover {
            border-radius: 12px;
          }

          & > .vertical-tile {
            width: 300px;
            @media (max-width: 330px) {
              width: 280px;
            }
            height: 100%;
            padding: 16px;
            border-radius: 16px;
            border: 1px;
            border: 1px solid #dfdfdf;
            font-family: "system-ui";
            display: grid;
            grid-template-rows: 200px 22px 48px 56px repeat(3, auto) 40px;
            grid-template-columns: 1fr 80px;
            & .lazyload-wrapper {
              grid-column: 1 / 3;
              & img {
                border-radius: 8px;
              }
              &.tour-map-image-wrapper {
                position: absolute;
                & > img {
                  width: 266px;
                  height: 200px;
                  object-fit: cover;
                  transform: scale(0);
                  transition: transform 0.2s ease;
                }
                &.show-map > img {
                  transform: scale(1);
                  transition: transform 0.2s ease;
                }
              }
            }
            & > .banner {
              z-index: 2;
              font-size: 14px;
              font-weight: 700;
              line-height: 18px;
              position: absolute;
              width: 100%;
              text-align: center;
              top: 50px;
              left: -80px;
              padding: 10px 0;
              color: white;
              font-weight: bold;
              transform: rotate(-45deg);
              text-transform: uppercase;
              background-color: #3a6968; // Natasha
              &.sold-out {
                background-color: #cf2f43; // Natasha
              }
              clip-path: polygon(93px 0px, 206px 0px, 244px 100%, 55px 100%);
            }
            // Lifetime Deposit
            & .lifetime-deposit,
            & .image-toggle-button {
              background: ${props => props.theme.white};
              border-radius: 8px;
              border: 1px solid #b7d7cd; // Natasha
              color: #315254; // Natasha
              font-size: 12px;
              font-weight: 500;
              line-height: 16px;
              text-align: center;
              padding: 4px 8px;
            }
            & .lifetime-deposit {
              position: absolute;
              top: 180px;
              right: 30px;
            }
            // Make buttons clickable
            & .image-toggle-button,
            & .details-button,
            & .lifetime-deposit {
              z-index: 2;
            }
            // Image toggle button positioning
            & .image-toggle-button {
              position: absolute;
              top: 180px;
              left: 30px;
            }
            & .tour-region {
              grid-column: 1 / 3;
              font-size: 12px;
              font-weight: 500;
              line-height: 16px;
              letter-spacing: 0px;
              color: #757575;
              margin: 6px 0;
            }
            & h2 {
              grid-column: 1 / 3;
              font-size: 16px;
              font-weight: 700;
              line-height: 24px;
              letter-spacing: 0.25px;
              margin: 0;
              display: -webkit-box;
              line-clamp: 2;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }
            & .tour-description {
              grid-column: 1 / 3;
              & > p {
                font-size: 13px;
                font-weight: 400;
                line-height: 21px;
                letter-spacing: 0.5px;
              }
              display: -webkit-box;
              line-clamp: 2;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
              margin: 8px 0;
            }
            & .core-icon-content {
              grid-column: 1 / 3;
              font-size: 12px;
              font-weight: 500;
              line-height: 16px;
              letter-spacing: 0px;
              text-align: left;
              color: #757575;
              & i {
                font-size: 12px;
                line-height: 16px;
                margin-right: 4px;
              }
              &:last-of-type {
                padding-bottom: 10px;
                border-bottom: 1px solid #dfdfdf;
              }
            }
            & .tour-price {
              grid-row: 8 / 9;
              grid-column: 1 / 2;
              font-size: 12px;
              align-self: end;
              color: #497e7a;
              & .tour-price-amount {
                font-size: 18px;
                font-weight: 700;
                line-height: 24px;
                & .currency-code {
                  margin-left: 4px;
                  font-size: 12px;
                }
              }
            }
            & .details-button {
              grid-row: 8 / 9;
              grid-column: 2 / 3;
              font-weight: 500;
              align-self: end;
              justify-self: end;
              width: 100%;
              &:hover {
                color: ${props.theme.primary};
              }
            }
          }
          @media only screen and (${props => props.theme.screen.small.min}) {
            &:hover {
              & > .vertical-tile {
                & .lazyload-wrapper.tour-map-image-wrapper > img {
                  transform: scale(1);
                  transition: transform 0.2s ease;
                }
              }
            }
            .image-toggle-button {
              display: none;
            }
          }
        `
      : undefined};

  @media (max-width: 450px) {
    .vertical-tile {
      width: 100%;
    }
    .lazyload-wrapper {
      img {
        width: 100% !important;
        max-height: 200px !important;
        min-height: 200px !important;
      }
    }
  }
`
const TourImage = styled(ImageWrapper)`
  @media only screen and (${props => props.theme.screen.tablet.max}) {
    flex: 0 0 120px;
    height: 185px;
  }
  @media only screen and (${props => props.theme.screen.small.min}) {
    flex: 0 0 267px;
    height: 200px;
  }
`
const TileImage = styled.div`
  position: relative;
  & > .map-button {
    display: block;
    position: absolute;
    bottom: 10px;
    left: 12px;
  }
  @media only screen and (${props => props.theme.screen.small.min}) {
    height: 200px;
    flex: 0 0 267px;
  }
  @media only screen and (${props => props.theme.screen.tablet.max}) {
    flex: 0 0 120px;
    height: 185px;
  }
  ${props =>
    props.$variant === "full-width-small"
      ? css`
          @media only screen and (${props => props.theme.screen.large.max}) {
            & > .map-button {
              bottom: 10px;
              left: 12px;
            }
          }
        `
      : undefined}
`
const Tags = styled(TagList)`
  grid-row: 1 / 2;
  grid-column: 1 / 3;
  margin-left: -4px;
  display: flex;
  wrap: nowrap;
  align-items: flex-start;
  max-width: 268px;
`
const Title = styled.h2`
  grid-row: 2 / 3;
  grid-column: 1 / 3;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  a {
    text-decoration: none;
    color: inherit;
  }
`
const Description = styled(HtmlContent)``
const Duration = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
`
const DurationIcon = styled(Icon)`
  &.icon {
    font-size: 14px;
    margin-right: 4px;
  }
`
const DurationValue = styled.span`
  vertical-align: middle;
  line-height: 14px;
`
const Price = styled.div`
  font-size: 10px;
  line-height: 15px;
`
const PriceAmount = styled(Currency)`
  font-weight: bold;
  font-size: 12px;
  ${props => props.discount && "text-decoration: line-through;"}
`
const DiscountPriceAmount = styled(PriceAmount)`
  color: ${props => props.theme.red};
`
const Calendar = styled.div`
  display: none;

  & > div {
    line-height: 23px;
  }
`
const ViewMoreButton = styled(Button)`
  z-index: 2;
`
const ViewMapButton = styled(Button)`
  z-index: 2;
`
const TourDetails = styled.div`
  overflow: hidden;
  flex: 1 1 100%;
  padding: 8px 4px 8px 6px;

  display: grid;
  grid-template-rows: 17px auto auto 15px 15px;
  // Required for Safari & Mozilla tile fit on mobile view
  grid-template-columns: repeat(2, minmax(100px, 1fr));

  & ${Description} {
    // Reference: https://css-tricks.com/almanac/properties/l/line-clamp/
    display: -webkit-box;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    grid-row: 3 / 4;
    grid-column: 1 / 3;

    // Required for line-clamp on Firefox & Safari
    & > p {
      display: inline;
    }
  }

  & ${Duration} {
    text-align: left;
    grid-row: 4 / 5;
    grid-column: 1 / 2;
  }

  & ${Price} {
    grid-row: 5 / 6;
    grid-column: 1 / 2;
  }

  & ${ViewMoreButton} {
    grid-row: 4 / 6;
    align-self: center;
    justify-self: end;
  }

  @media only screen and (${props => props.theme.screen.small.min}) {
    grid-template-rows: 31px 27px auto 1fr;
    grid-template-columns: 264px auto 96px;
    padding: 10px;
    & ${Description} {
      grid-row: 3 / 4;
      grid-column: 1 / 3;
      display: -webkit-box;
      line-clamp: 3;
      -webkit-line-clamp: 3;
    }
    & ${Duration} {
      grid-row: 1 / 2;
      grid-column: 3 / 4;
      text-align: right;
      align-self: end;
      line-height: 20px;
      padding-bottom: 3px;
    }
    & ${DurationIcon} {
      font-size: 16px;
    }
    & ${DurationValue} {
      line-height: 16px;
    }
    & ${Price} {
      grid-row: 2 / 4;
      grid-column: 3 / 4;
      font-size: 12px;
      line-height: 24px;
      text-align: right;
      & > span {
        display: block;
      }
    }
    & ${PriceAmount} {
      font-size: 16px;
    }
    & ${Calendar} {
      display: block;
      align-self: end;
      grid-row: 4 / 5;
      grid-column: 1 / 3;
    }
    & ${ViewMoreButton} {
      grid-row: 4 / 5;
      align-self: end;
    }
  }

  @media only screen and (${props =>
      props.theme.screen.medium.min}) and (${props =>
      props.theme.screen.medium.max}) {
    ${props =>
      props.variant === "full-width-small"
        ? css`
            grid-template-rows: 30px auto 20px auto 20px 20px;
            grid-template-columns: 70% 30%; // Needed to avoid discounted prices showing in a row
            padding: 10px;
            & ${Calendar} {
              display: block;
              align-self: center;
              grid-row: 4 / 5;
              grid-column: 1 / 3;
            }
            & ${Duration} {
              line-height: 20px;
              text-align: left;
              grid-row: 5 / 6;
              grid-column: 1 / 2;
            }
            & ${DurationIcon} {
              font-size: 16px;
            }
            & ${DurationValue} {
              line-height: 16px;
            }
            & ${Price} {
              grid-row: 6 / 7;
              grid-column: 1 / 2;
              font-size: 12px;
              line-height: 20px;
              text-align: left;
              padding-top: 3px;
              display: flex;
            }
            & ${PriceAmount} {
              font-size: 16px;
            }
            & ${ViewMoreButton} {
              grid-row: 5 / 7;
              align-self: center;
              justify-self: end;
            }
          `
        : ""}
  }
`
const DetailTile = styled.div`
  overflow: hidden;
  background-color: ${props => props.theme.bg1};
  box-shadow: 0 2px 4px 3px rgba(0, 0, 0, 0.15);
  border-radius: 4px;

  display: flex;
  flex: 0 1 100%;
  margin: 0;
  flex-direction: column;

  @media only screen and (${props => props.theme.screen.tablet.min}) {
    flex: 0 0 100%;
  }

  @media only screen and (${props => props.theme.screen.small.min}) {
    flex-direction: row;
    border-radius: 8px;
    flex: 0 0 100%;
    height: 200x;
  }

  @media only screen and (${props => props.theme.screen.medium.min}) {
    flex: 0 0 calc(50% - 10px);
  }

  @media only screen and (${props => props.theme.screen.desktop.min}) {
    flex: 0 0 634px;
  }

  ${props =>
    props.variant === "full-width" || props.variant === "full-width-small"
      ? css`
          @media only screen and (${props => props.theme.screen.medium.min}) {
            border-radius: 8px;
            flex: 0 0 100%;
            min-height: 150px;
            & ${Description} {
              font-size: 14px;
              line-height: 20px;
            }
          }
          @media only screen and (${props => props.theme.screen.large.min}) {
            flex: 0 0 ${props.variant === "full-width-small" ? "100%" : "870px"};
          }
          ${props =>
            props.variant === "full-width" &&
            css`
              @media only screen and (${props =>
                  props.theme.screen.desktop.min}) {
                flex: 0 0 990px;
              }
            `}
        `
      : undefined}
`
const TourDetailTile = ({ tour, variant }) => {
  const [photoDisplay, setPhotoDisplay] = useState("photo")
  const terms = checkTourTnC(tour?.tourOperator?.operatorTnC, tour?.tourTnC)
  const tourTags = []
  if (tour.region) {
    tourTags.push({
      group: "region",
      value: tour.region.name,
      link: tour.region.uri,
    })
  }
  if (tour.category) {
    tourTags.push({
      group: "activity",
      value: tour.category.name,
      link: tour.category.uri.replace(/^\/activity\//, "/tours/"),
    })
  }
  if (tour.tourType) {
    tourTags.push({
      group: "type",
      value: tour.tourType.name,
    })
  }
  const showViewMap = tour.map?.image?.title !== DUMMY_MAP_TITLE
  const { alt, src, sizes } = tour.carousel.imageFront
  let thumbnailSrc
  let srcSet = ""
  if (sizes && sizes.length) {
    sizes.forEach((image, index) => {
      if (index === 0) {
        thumbnailSrc = image.url
      }
      srcSet += `${image.url} ${index + 1}x${
        index !== sizes.length - 1 ? `,` : ""
      }`
    })
  }
  let tourContent = tour.content.split("\n", 2)[0]
  if (tourContent.charAt(0) !== "<") {
    tourContent = "<p>" + tourContent + "</p>"
  }

  const WrapperResolver = ({ children }) => {
    return tour.link ? (
      <LinkWrapper href={tour.uri} target="_blank">
        <StyledClickableWrapper
          title={`Open details for ${tour.name}`}
          external
          $variant={variant}
        >
          {children}
        </StyledClickableWrapper>
      </LinkWrapper>
    ) : (
      <StyledClickableWrapper
        to={tour.uri}
        title={`Open details for ${tour.name}`}
        external
        $variant={variant}
      >
        {children}
      </StyledClickableWrapper>
    )
  }

  return (
    <WrapperResolver>
      {variant === "vertical" ? (
        <div className="vertical-tile">
          {/* NOTE: width, height and src conditionals are to account for differences in size + structure of information coming from GraphQL versus Lambda after filter use */}
          {tour.spotsRemaining && (
            <div
              className={`banner ${
                tour.spotsRemaining === "Sold Out" ? "sold-out" : "few-spots"
              }`}
            >
              {tour.spotsRemaining}
            </div>
          )}
          {terms && terms.lifetime && (
            <span className="lifetime-deposit">Lifetime Deposit</span>
          )}
          <Image
            width={sizes && sizes.length ? sizes[0].width : 266}
            height={sizes && sizes.length ? sizes[0].height : 177}
            alt={alt}
            src={sizes && sizes.length ? thumbnailSrc : src}
            srcSet={srcSet.length && srcSet}
          />
          {tour.map && tour.map.image && showViewMap && (
            <Image
              width={266}
              height={200}
              alt={tour.map.image.alt}
              src={tour.map.image.src}
              wrapperProps={{
                className: `tour-map-image-wrapper ${
                  photoDisplay ? "" : "show-map"
                }`,
              }}
            />
          )}
          {tour.map && tour.map.image && showViewMap && (
            <Button
              className="image-toggle-button"
              mobileSizeOnly
              onClick={() => setPhotoDisplay(prev => !prev)}
            >
              See {photoDisplay ? "map" : "photo"}
            </Button>
          )}
          <p className="tour-region">{tour.region.name}</p>
          <h2>{tour && `${tour.name}`}</h2>
          {tour.content && (
            <HtmlContent className="tour-description" html={tourContent} />
          )}
          <IconDetailContentTile
            glyph={TOUR_CATEGORY_ICONS[tour.category.name] || "v2-calendar"}
            color="gray"
          >
            {tour.category.name}
          </IconDetailContentTile>
          <IconDetailContentTile glyph={"v2-backpack"} color="gray">
            {`${tour.tourType.name}`}
          </IconDetailContentTile>
          {tour.available && (
            <IconDetailContentTile glyph={"v2-calendar"} color="gray">
              {convertToMonths(tour.available)}
            </IconDetailContentTile>
          )}

          <span className="tour-price">
            {`${tour.duration} days from `}
            {tour.price && (
              <Currency
                className="tour-price-amount"
                amount={tour.price.amount}
                code={tour.price?.currency?.code}
                showAbbr="after"
              />
            )}
          </span>
          <Button
            className="details-button"
            color="black-white"
            size="x-small"
            href={tour.uri}
            mobileSizeOnly
            external
          >
            Details
          </Button>
        </div>
      ) : (
        <DetailTile variant={variant}>
          <TileImage $variant={variant}>
            <Image
              wrapper={TourImage}
              {...tour.carousel?.imageFront}
              showCaption={false}
              artDirection
            />
            {tour.map && tour.map.image && showViewMap && (
              <ImageGallery
                variant="popup"
                images={[{ ...tour.map?.image }]}
                toggleButton={
                  <ViewMapButton
                    size="x-small"
                    color="gray"
                    mobileSizeOnly
                    className="map-button"
                  >
                    View Map
                  </ViewMapButton>
                }
              />
            )}
          </TileImage>
          <TourDetails variant={variant}>
            <Title className="h4">{tour && `${tour.name}`}</Title>
            <Tags tags={tourTags} />
            <Duration>
              <DurationIcon glyph="time" />
              <DurationValue>{`${tour.duration} days`}</DurationValue>
            </Duration>
            <Price>
              <span>From </span>
              <PriceAmount
                amount={
                  tour.price?.amountBase
                    ? tour.price?.amountBase
                    : tour.price?.amount
                }
                code={tour.price?.currency?.code}
                showAbbr={
                  variant === "full-width" || variant === "full-width-small"
                }
                discount={tour.price?.discount}
                applyRounding={!tour.price?.discount ? "auto" : false}
              />
              {tour.price?.discount &&
              tour.price?.amount &&
              tour.price?.amountBase ? (
                <>
                  {" "}
                  <DiscountPriceAmount
                    amount={tour.price?.amount}
                    code={tour.price?.currency?.code}
                    showAbrr={
                      variant === "full-width" || variant === "full-width-small"
                    }
                    applyRounding={false}
                  />
                </>
              ) : undefined}
            </Price>
            <Calendar>
              <h5>Months Tour Operates</h5>
              <CalendarMonths available={tour.available} primary />
            </Calendar>
            <Description
              html={tour.content ? tour.content.split("\n", 2)[0] : undefined}
            />

            <ViewMoreButton
              color="purple"
              size="x-small"
              href={tour.uri}
              mobileSizeOnly
              multiline
              external
            >
              View More
            </ViewMoreButton>
          </TourDetails>
        </DetailTile>
      )}
    </WrapperResolver>
  )
}
export const TourDetailTilePropTypes = {
  name: PropTypes.string,
  price: PropTypes.shape({
    ...PricePropTypes,
  }),
  duration: PropTypes.number,
  content: PropTypes.string,
  category: PropTypes.shape({
    name: PropTypes.string,
    uri: PropTypes.string,
  }),
  region: PropTypes.shape({
    name: PropTypes.string,
    uri: PropTypes.string,
  }),
  spotsRemaining: PropTypes.string,
  tourType: PropTypes.shape({
    name: PropTypes.string,
    uri: PropTypes.string,
  }),
  available: PropTypes.arrayOf(PropTypes.number),
  image: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  map: PropTypes.shape({
    image: PropTypes.shape({ ...ImagePropTypes, title: PropTypes.string }),
  }),
  carousel: PropTypes.shape({
    imageFront: PropTypes.shape({ ...ImagePropTypes }),
  }),
  uri: PropTypes.string,
  link: PropTypes.bool,
  tourTnC: PropTypes.shape({
    lifetime: PropTypes.bool,
    worse: PropTypes.bool,
    text: PropTypes.string,
  }),
  tourOperator: PropTypes.shape({
    title: PropTypes.string,
    uri: PropTypes.string,
    operatorTnC: PropTypes.shape({
      lifetime: PropTypes.bool,
      worse: PropTypes.bool,
      text: PropTypes.string,
    }),
  }),
}

TourDetailTile.propTypes = {
  tour: PropTypes.shape(TourDetailTilePropTypes),
  variant: PropTypes.oneOf(["full-width", "full-width-small", "vertical"]),
}
export default TourDetailTile

export const TourDetailTileQuery = graphql`
  fragment TenToursGQL_TourDetailTileQuery on TenToursGQL_Tour {
    id: databaseId
    title
    slug
    uri
    content
    tourMonths
    modified
    price(currency: $currency) {
      ...PriceFragment
    }
    spotsRemaining
    tourDuration {
      days
    }
    tourCategory {
      name
      uri
    }
    tourType {
      name
      uri
    }
    tourRegion {
      name
      uri
    }
    tourOperator {
      operatorTnC: termsConditions {
        lifetime
        text
        worse
      }
    }
    tourTnC {
      lifetime
      text
      worse
    }
    carousel {
      imageFront {
        ...TenToursGQL_ImageSizesQuery
        sizes {
          height
          name
          url
          width
        }
      }
    }
    map {
      image {
        title
        ...TenToursGQL_ImageSizesQuery
      }
    }
  }
`
